import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../services/api";
import Button from "../common/Button";

interface MonthlySummary {
  month?: string; // YYYY-MM
  from?: string;
  to?: string;
  revenue?: number;
  expenditure?: number;
  net?: number;
}

const monthLabel = (month: string) => {
  const [y, m] = (month || "").split("-");
  const year = Number(y);
  const mon = Number(m);
  if (!Number.isFinite(year) || !Number.isFinite(mon) || mon < 1 || mon > 12) return month || "This month";
  const d = new Date(year, mon - 1, 1);
  return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
};

export default function MonthlyCollectionWidget() {
  const now = useMemo(() => new Date(), []);
  const currentMonth = useMemo(
    () => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    [now]
  );

  const [data, setData] = useState<MonthlySummary>({
    month: currentMonth,
    revenue: 0,
    expenditure: 0,
    net: 0,
  });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = (await apiGet(`/api/pharmacy/monthly-summary?month=${encodeURIComponent(currentMonth)}`)) as MonthlySummary;
      setData({
        month: res?.month || currentMonth,
        from: res?.from,
        to: res?.to,
        revenue: Number(res?.revenue || 0),
        expenditure: Number(res?.expenditure || 0),
        net: Number(res?.net || 0),
      });
    } catch (e) {
      console.error("Failed to load monthly pharmacy summary", e);
      setData({ month: currentMonth, revenue: 0, expenditure: 0, net: 0 });
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onBill = () => load();
    const onPaid = () => load();
    window.addEventListener("pharmacy:billSaved", onBill);
    window.addEventListener("pharmacy:supplierPaid", onPaid);
    return () => {
      window.removeEventListener("pharmacy:billSaved", onBill);
      window.removeEventListener("pharmacy:supplierPaid", onPaid);
    };
  }, [load]);

  const revenue = Number(data.revenue || 0);
  const expenditure = Number(data.expenditure || 0);
  const net = Number(data.net || 0);
  return (
    <div style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: 'var(--mod-shadow-sm)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Monthly Summary</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>{monthLabel(String(data.month || currentMonth))}</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '2px' }}>
            {data.from && data.to ? `${data.from} → ${data.to}` : "Till today"}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} isLoading={loading}>↻ Refresh</Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>Revenue</div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Patient bills</div>
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#16a34a' }}>₹{revenue.toLocaleString("en-IN")}</div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>Expenditure</div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Supplier payments</div>
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#dc2626' }}>₹{expenditure.toLocaleString("en-IN")}</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: net >= 0 ? '#f0fdf4' : '#fef2f2', padding: '12px', borderRadius: '10px', border: net >= 0 ? '1px solid #bbf7d0' : '1px solid #fecaca' }}>
          <div style={{ fontSize: '0.9rem', color: net >= 0 ? '#166534' : '#991b1b', fontWeight: 700 }}>Net Cashflow</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 900, color: net >= 0 ? '#15803d' : '#dc2626' }}>₹{net.toLocaleString("en-IN")}</div>
        </div>
      </div>
    </div>
  );
}

